import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { useI18n } from "@wato/i18n/react";
import { Alert, Button, Icon, IconButton, buttonVariantClasses, cx } from "@wato/ui";
import { useApi } from "../../api/context";
import type { AiAgentView } from "../../api/automation-types";
import { ChatPreview, type ChatPreviewMessage } from "../../components/ChatPreview";
import { cleanAgentReply } from "../ai/agents/clean-agent-reply";

type Turn = { role: "user" | "assistant"; text: string };

/**
 * FB2-49: first value without a WhatsApp number. A made-up customer chats with the workspace's
 * first AI agent through the sandbox endpoint the AI test console uses (`testAiAgent`): nothing is
 * sent to WhatsApp or charged, and the panel is always labelled "Simulation".
 */
export function DemoConversation({
  workspaceId,
  businessName,
  onTried,
  onClose,
}: {
  readonly workspaceId: string;
  readonly businessName: string;
  readonly onTried: () => void;
  readonly onClose: () => void;
}) {
  const { t } = useI18n();
  const api = useApi();
  const [agents, setAgents] = useState<AiAgentView[] | null>(null);
  const [chat, setChat] = useState<Turn[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    api.listAiAgents(workspaceId).then(
      (a) => live && setAgents(a),
      () => live && setAgents([]),
    );
    return () => {
      live = false;
    };
  }, [api, workspaceId]);

  const agent = agents?.find((a) => a.status === "published") ?? agents?.[0];

  const send = async (raw: string) => {
    const text = raw.trim();
    if (!text || !agent || busy) return;
    const next: Turn[] = [...chat, { role: "user", text }];
    setChat(next);
    setInput("");
    setBusy(true);
    setFailed(false);
    try {
      const res = await api.testAiAgent(workspaceId, agent.id, next);
      setChat([
        ...next,
        { role: "assistant", text: cleanAgentReply(res.reply) ?? t("setup.demo.handoff") },
      ]);
      onTried();
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    void send(input);
  };

  const messages: ChatPreviewMessage[] = chat.map((m) => ({
    from: m.role === "user" ? "customer" : "business",
    text: m.text,
  }));

  return (
    <section
      aria-label={t("setup.demo.title")}
      data-testid="demo-conversation"
      className="flex flex-col gap-3 rounded-md border border-line p-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="text-sm font-semibold text-fg">{t("setup.demo.title")}</h4>
        <span
          data-testid="demo-simulation-label"
          className="rounded-full bg-surface-muted px-2 py-0.5 text-xs font-medium text-fg"
        >
          {t("setup.demo.badge")}
        </span>
        <IconButton
          variant="ghost"
          label={t("setup.demo.close")}
          icon={<Icon name="close" />}
          onClick={onClose}
        />
      </div>
      <p role="note" className="text-xs text-fg-muted">
        {t("setup.demo.note")}
      </p>
      {agents && !agent ? (
        <Alert tone="info">
          <div className="flex flex-wrap items-center gap-3" data-testid="demo-blocked">
            <span>{t("setup.demo.blocked.reason")}</span>
            <Link
              to={`/w/${workspaceId}/ai-agents/new`}
              className={cx(
                "inline-flex h-9 items-center rounded-md px-3 text-sm font-medium",
                buttonVariantClasses.secondary,
              )}
            >
              {t("setup.demo.blocked.cta")}
            </Link>
          </div>
        </Alert>
      ) : agent ? (
        <>
          <p className="text-xs text-fg-muted">
            {t("setup.demo.agentLabel", { name: agent.name })}
          </p>
          {messages.length > 0 ? (
            <ChatPreview
              messages={messages}
              businessName={businessName}
              caption={t("setup.demo.note")}
              testId="demo-chat"
            />
          ) : null}
          <div className="flex flex-wrap gap-2">
            {(["hours", "price", "order"] as const).map((k) => (
              <Button
                key={k}
                size="sm"
                variant="secondary"
                disabled={busy}
                onClick={() => void send(t(`setup.demo.suggestions.${k}`))}
              >
                {t(`setup.demo.suggestions.${k}`)}
              </Button>
            ))}
          </div>
          <form onSubmit={onSubmit} className="flex flex-wrap items-center gap-2">
            <input
              aria-label={t("setup.demo.inputLabel")}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="h-9 min-w-0 flex-1 rounded-md border border-line bg-surface px-3 text-sm"
            />
            <Button size="sm" type="submit" disabled={busy || !input.trim()}>
              {busy ? t("setup.demo.sending") : t("setup.demo.send")}
            </Button>
          </form>
          {failed ? <span role="alert">{t("setup.demo.error")}</span> : null}
        </>
      ) : null}
    </section>
  );
}
